<template>
  <SideSheet
    :is-open="props.isOpen"
    position="right"
    container-class="screen-size"
    content-size="md"
    :content-color="NebraskaColors.backgroundTertiary"
    @close="handleClose"
    @bottom="handleScrollBottom"
  >
    <div class="sheet" data-testid="notification-central-sheet">
      <div class="sheet__header">
        <ButtonIconContextual
          data-testid="notification-central-close"
          label=""
          icon="EF0030"
          @click.native="handleClose"
        />
      </div>
      <div class="sheet__container">
        <Heading :level="2">Notificações</Heading>
        <SpinnerLoader
          v-if="isLoading && pagination.pageIndex === 1"
          class="sheet__loading"
          :size="48"
        />
        <div
          v-else-if="!isEmpty && !hasError"
          class="sheet__list"
          data-testid="notification-central-list"
        >
          <div
            v-for="(record, index) in notificationsData"
            :key="index"
            class="sheet__list-item"
          >
            <BaseText size="large">
              {{ record.header.title }}
            </BaseText>
            <div
              v-for="notification in record.notifications"
              :key="notification.id"
              class="sheet__card"
              :class="computeClass(notification)"
              @click="handleClick(notification)"
            >
              <div class="sheet__card-header">
                <BaseText
                  :title="notification.title"
                  class="sheet__card-title"
                  :color="
                    notification.read
                      ? NebraskaColors.elementSecondary
                      : NebraskaColors.textPrimary
                  "
                  size="md"
                >
                  {{ notification.title }}
                </BaseText>
                <BaseText class="sheet__card-hour" size="xs">
                  {{ formatNotificationTimeToLocal(notification.date) }}
                </BaseText>
              </div>
              <BaseText class="sheet__card-description" size="xs">
                {{ notification.description }}
              </BaseText>
            </div>
          </div>
          <SpinnerLoader v-if="isLoading" class="sheet__loading" :size="48" />
        </div>
        <div v-else-if="hasError" class="sheet__error">
          <div class="sheet__round-icon sheet__round-icon-warn">
            <BaseIcon icon="EF0062" width="24" />
          </div>
          <div class="sheet__error-header">
            <Heading :level="3"
              >Não foi possível carregar os dados neste momento</Heading
            >
            <BaseText class="sheet__error-disclaimer" size="md">
              Aguarde alguns instantes e tente novamente
            </BaseText>
          </div>
          <ButtonSecondary
            class="sheet__error-button"
            label="Tentar novamente"
            @click="retry"
          />
        </div>
        <div v-else class="sheet__empty">
          <div class="sheet__round-icon">
            <BaseIcon icon="DA0060" width="24" />
          </div>
          <BaseText class="sheet__empty-disclaimer" size="md">
            Você não possui notificações
          </BaseText>
        </div>
      </div>
    </div>
  </SideSheet>
</template>

<script setup lang="ts">
import { formatNotificationTimeToLocal } from '@/modules/common/helpers/date';
import { ref, watch } from 'vue';

import {
  Heading,
  ButtonIconContextual,
  BaseIcon,
  ButtonSecondary,
  SpinnerLoader,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import { SideSheet } from '@/modules/common/components/SideSheet';
import {
  NotificationRecord,
  getNotifications,
  postReadNotifications,
  Notification,
  NotificationQueryParams
} from '@/modules/common/services/new-notifications';

const PAGE_INITIAL = {
  pageIndex: 1,
  pageSize: 15
};

const notificationsData = ref<NotificationRecord[]>([]);
const pagination = ref<NotificationQueryParams>(PAGE_INITIAL);
const isLastPage = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const hasError = ref<boolean>(false);
const isEmpty = ref<boolean>(false);

const emit = defineEmits(['close', 'clearUnread']);

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  unreadCounter: {
    type: Number,
    default: 0
  }
});

watch(
  () => props.isOpen,
  async isOpen => {
    if (isOpen) {
      retry();
    }
  }
);

const retry = async () => {
  await fetchNotifications();
  if (!hasError.value && props.unreadCounter > 0) {
    postReadNotifications();
    emit('clearUnread');
  }
};

const fetchNotifications = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;

    const data = await getNotifications(pagination.value);
    notificationsData.value.push(...data.records);
    isLastPage.value = data.pagination.isLastPage;
    isEmpty.value = data.records.length === 0;
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const resetCentral = () => {
  pagination.value = PAGE_INITIAL;
  notificationsData.value = [];
  isLastPage.value = false;
  isLoading.value = false;
  hasError.value = false;
  isEmpty.value = false;
};

const handleClose = () => {
  resetCentral();
  emit('close');
};

const handleClick = (notification: Notification) => {
  if (notification.url) {
    window.open(notification.url, '_self');
  }
};

const computeClass = (notification: Notification) => {
  return {
    'sheet__card-read': notification.read,
    'sheet__card-click': !!notification.url
  };
};

const handleScrollBottom = () => {
  if (
    !isEmpty.value &&
    !isLastPage.value &&
    !isLoading.value &&
    !hasError.value
  ) {
    pagination.value.pageIndex = pagination.value.pageIndex + 1;
    fetchNotifications();
  }
};
</script>

<style lang="less" scoped>
.sheet {
  height: auto;
  overflow-y: auto;
  text-align: left;
  color: @text-primary;

  &__header {
    padding: @size-spacing-x300;
    height: 64px;
    width: 100%;
  }

  &__container {
    padding: @size-spacing-x1000 @size-spacing-x1200;
    width: 100%;
  }

  &__list {
    padding-top: @size-spacing-x800;

    &-item {
      padding-bottom: @size-spacing-x600;
    }
  }

  &__loading {
    margin: @size-spacing-x400 auto;
    display: flex;
  }

  &__empty {
    padding: @size-spacing-x2000 @size-spacing-x900;
    text-align: center;

    &-disclaimer {
      padding-top: @size-spacing-x400;
    }
  }

  &__round-icon {
    width: 64px;
    height: 64px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 100%;
    color: @element-primary;
    background-color: @neutral-secondary;

    &-warn {
      color: @status-alert;
      background-color: @status-alert-background;
    }
  }

  &__error {
    padding: @size-spacing-x2000 @size-spacing-x900;
    text-align: center;

    &-header {
      padding-top: @size-spacing-x400;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    &-disclaimer {
      padding-top: @size-spacing-x300;
    }

    &-button {
      margin-top: @size-spacing-x400;
    }
  }

  &__card {
    margin-top: @size-spacing-x400;
    padding: @size-spacing-x450 @size-spacing-x500;
    background-color: @background-secondary;
    border-radius: @size-radius-x300;

    &-read {
      border: 1px @divider-primary solid;
      background-color: transparent;
    }

    &-click {
      cursor: pointer;
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &-title {
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-hour {
      margin: 0;
      text-align: right;
    }

    &-description {
      margin-top: @size-spacing-x150;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  }
}
</style>
